import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  confirmForgotPassword,
  confirmSignUp,
  forgotPassword,
  getSession,
  resendConfirmationCode,
  signIn,
  signUp,
} from "@/integrations/aws/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { emailSchema } from "@/lib/email";
import { notify } from "@/lib/notify";
import { cn } from "@/lib/utils";
import { z } from "zod";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  Mail,
  Receipt,
  ShieldCheck,
  User,
  Users,
  type LucideIcon,
} from "lucide-react";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Sign in — Somasi Ops" },
      {
        name: "description",
        content: "Sign in to Somasi Ops to manage client groups, companies and contacts.",
      },
      { property: "og:title", content: "Sign in — Somasi Ops" },
      {
        property: "og:description",
        content: "Sign in to Somasi Ops to manage client groups, companies and contacts.",
      },
    ],
  }),
  /*
   * Where to go once signed in. Set by the signed-in guard when it turns a
   * visitor away — an emailed link into the app has to land where it pointed,
   * not on the dashboard. Only a path inside the app is honoured.
   */
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const target = typeof search.redirect === "string" ? search.redirect : "";
    return target.startsWith("/") && !target.startsWith("//") ? { redirect: target } : {};
  },
  component: AuthPage,
});

// Cognito's password policy: 8+ characters with upper and lower case and a digit.
const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .max(72)
  .regex(/[a-z]/, "Password must include a lower-case letter")
  .regex(/[A-Z]/, "Password must include an upper-case letter")
  .regex(/[0-9]/, "Password must include a number");

const passwordRules = [
  { label: "8+ characters", test: (pw: string) => pw.length >= 8 },
  { label: "Upper case", test: (pw: string) => /[A-Z]/.test(pw) },
  { label: "Lower case", test: (pw: string) => /[a-z]/.test(pw) },
  { label: "A number", test: (pw: string) => /[0-9]/.test(pw) },
];

const features: { icon: LucideIcon; text: string }[] = [
  { icon: Users, text: "Manage client groups, companies and contacts" },
  { icon: Receipt, text: "Run payroll and send payslips" },
  { icon: ShieldCheck, text: "Secured by AWS Cognito" },
];

type View = "auth" | "confirm" | "forgot" | "reset";

function parse(schema: z.ZodTypeAny, value: string) {
  const result = schema.safeParse(value);
  if (!result.success) notify.error(result.error.issues[0]?.message ?? "Invalid input");
  return result.success;
}

/** Input with a leading icon. */
function IconInput({
  icon: Icon,
  className,
  ...props
}: React.ComponentProps<typeof Input> & { icon: LucideIcon }) {
  return (
    <div className="relative">
      <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input {...props} className={cn("h-10 pl-9", className)} />
    </div>
  );
}

function PasswordInput(props: Omit<React.ComponentProps<typeof Input>, "type">) {
  const [visible, setVisible] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const checkCaps = (e: React.KeyboardEvent<HTMLInputElement>) =>
    setCapsLock(e.getModifierState?.("CapsLock") ?? false);
  return (
    <div className="space-y-1.5">
      <div className="relative">
        <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          {...props}
          type={visible ? "text" : "password"}
          onKeyUp={checkCaps}
          onKeyDown={checkCaps}
          onBlur={() => setCapsLock(false)}
          className="h-10 pl-9 pr-10"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {capsLock && (
        <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5" /> Caps Lock is on
        </p>
      )}
    </div>
  );
}

function PasswordChecklist({ password }: { password: string }) {
  return (
    <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
      {passwordRules.map((rule) => {
        const ok = rule.test(password);
        return (
          <li
            key={rule.label}
            className={cn(
              "flex items-center gap-1.5 transition-colors",
              ok ? "text-primary" : "text-muted-foreground",
            )}
          >
            <span
              className={cn(
                "flex h-3.5 w-3.5 items-center justify-center rounded-full border",
                ok ? "border-primary bg-primary text-primary-foreground" : "border-border",
              )}
            >
              {ok && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
            </span>
            {rule.label}
          </li>
        );
      })}
    </ul>
  );
}

function SubmitButton({ loading, children }: { loading: boolean; children: React.ReactNode }) {
  return (
    <Button type="submit" className="h-10 w-full" disabled={loading}>
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </Button>
  );
}

function BackLink({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" /> {children}
    </button>
  );
}

function AuthPage() {
  const navigate = useNavigate();
  const { redirect: target } = Route.useSearch();
  const proceed = () =>
    target
      ? navigate({ href: target, replace: true })
      : navigate({ to: "/dashboard", replace: true });
  const [view, setView] = useState<View>("auth");
  const [tab, setTab] = useState("signin");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [code, setCode] = useState("");

  useEffect(() => {
    void getSession().then((session) => {
      if (session) void proceed();
    });
  }, [navigate]);

  const run = async (action: () => Promise<void>) => {
    setLoading(true);
    try {
      await action();
    } catch (error) {
      notify.error(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * The address as typed, trimmed and lowercased, or `null` after saying what is
   * wrong with it. The cleaned value goes back into the box too, so the confirm
   * and reset steps that follow send Cognito the same address this one did.
   */
  const cleanEmail = (): string | null => {
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      notify.error(result.error.issues[0]?.message ?? "Enter a valid email address");
      return null;
    }
    setEmail(result.data);
    return result.data;
  };

  const goTo = (next: View) => {
    setCode("");
    if (next === "reset" || next === "forgot") setPassword("");
    setView(next);
  };

  const handleSignIn = (e: React.FormEvent) => {
    e.preventDefault();
    const address = cleanEmail();
    if (!address || !parse(passwordSchema, password)) return;
    void run(async () => {
      await signIn(address, password);
      notify.success("Welcome back");
      void proceed();
    });
  };

  const handleSignUp = (e: React.FormEvent) => {
    e.preventDefault();
    const address = cleanEmail();
    if (!address || !parse(passwordSchema, password)) return;
    void run(async () => {
      const { confirmationRequired } = await signUp(
        address,
        password,
        fullName || address.split("@")[0]!,
      );
      if (confirmationRequired) {
        goTo("confirm");
        notify.success("Account created — enter the code we emailed you");
      } else {
        setTab("signin");
        notify.success("Account created — you can sign in now");
      }
    });
  };

  const handleConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return notify.error("Enter the confirmation code");
    void run(async () => {
      await confirmSignUp(email, code);
      await signIn(email, password);
      notify.success("Email confirmed — welcome");
      void proceed();
    });
  };

  const handleResend = () =>
    run(async () => {
      await resendConfirmationCode(email);
      notify.success("A new code is on its way");
    });

  const handleForgot = (e: React.FormEvent) => {
    e.preventDefault();
    const address = cleanEmail();
    if (!address) return;
    void run(async () => {
      await forgotPassword(address);
      goTo("reset");
      notify.success("If an account exists, a reset code is on its way");
    });
  };

  const handleResendReset = () =>
    run(async () => {
      await forgotPassword(email);
      notify.success("A new code is on its way");
    });

  const handleReset = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return notify.error("Enter the code from your email");
    if (!parse(passwordSchema, password)) return;
    void run(async () => {
      await confirmForgotPassword(email, code, password);
      notify.success("Password updated — sign in with your new password");
      setPassword("");
      setTab("signin");
      goTo("auth");
    });
  };

  const heading: Record<View, { title: string; description: string }> = {
    auth:
      tab === "signin"
        ? { title: "Welcome back", description: "Sign in to your workspace" }
        : { title: "Create your account", description: "Get started with Somasi Ops" },
    confirm: {
      title: "Check your email",
      description: `Enter the code we sent to ${email || "your email"}`,
    },
    forgot: {
      title: "Reset your password",
      description: "Enter your email and we'll send you a reset code",
    },
    reset: {
      title: "Choose a new password",
      description: `Enter the code we sent to ${email || "your email"}`,
    },
  };

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-[5fr_6fr]">
      {/* Brand panel */}
      <aside className="relative hidden overflow-hidden bg-primary p-10 text-primary-foreground lg:flex lg:flex-col lg:justify-between">
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary-foreground/5" />
        <div className="pointer-events-none absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-primary-foreground/5" />

        <Link to="/" className="relative flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-foreground shadow-sm">
            <img src="/somasi-mark.png" alt="" className="h-7 w-7 object-contain" />
          </span>
          <span className="leading-tight">
            <span className="block text-base font-semibold">Somasi Ops</span>
            <span className="block text-xs text-primary-foreground/70">Corporate Services</span>
          </span>
        </Link>

        <div className="relative max-w-md space-y-8">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">
            Client groups, payroll and people, in one place.
          </h2>
          <ul className="space-y-4">
            {features.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-sm text-primary-foreground/85">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-foreground/10">
                  <Icon className="h-4 w-4" />
                </span>
                {text}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-primary-foreground/60">
          © {new Date().getFullYear()} Somasi Corporate Services
        </p>
      </aside>

      {/* Form panel */}
      <main className="flex items-center justify-center px-4 py-10 sm:px-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="mb-8 flex items-center justify-center gap-2 lg:hidden">
            <img src="/somasi-mark.png" alt="" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="text-sm font-semibold">Somasi Ops</div>
              <div className="text-[11px] text-muted-foreground">Corporate Services</div>
            </div>
          </Link>

          {view !== "auth" && (
            <div className="mb-6">
              <BackLink onClick={() => goTo(view === "reset" ? "forgot" : "auth")}>
                {view === "reset" ? "Use a different email" : "Back to sign in"}
              </BackLink>
            </div>
          )}

          {view !== "auth" && (
            <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-primary">
              {view === "forgot" ? <KeyRound className="h-5 w-5" /> : <Mail className="h-5 w-5" />}
            </span>
          )}

          <div className="mb-6 space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">{heading[view].title}</h1>
            <p className="text-sm text-muted-foreground">{heading[view].description}</p>
          </div>

          {view === "auth" && (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid h-10 w-full grid-cols-2">
                <TabsTrigger value="signin">Sign in</TabsTrigger>
                <TabsTrigger value="signup">Create account</TabsTrigger>
              </TabsList>

              <TabsContent value="signin" className="mt-6">
                <form className="space-y-4" onSubmit={handleSignIn}>
                  <div className="space-y-2">
                    <Label htmlFor="si-email" required>
                      Email
                    </Label>
                    <IconInput
                      icon={Mail}
                      id="si-email"
                      type="email"
                      autoComplete="email"
                      placeholder="name@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="si-pw" required>
                        Password
                      </Label>
                      <button
                        type="button"
                        onClick={() => goTo("forgot")}
                        className="text-xs font-medium text-primary underline-offset-4 hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <PasswordInput
                      id="si-pw"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                  <SubmitButton loading={loading}>
                    {loading ? "Signing in…" : "Sign in"}
                  </SubmitButton>
                </form>
              </TabsContent>

              <TabsContent value="signup" className="mt-6">
                <form className="space-y-4" onSubmit={handleSignUp}>
                  <div className="space-y-2">
                    <Label htmlFor="su-name">Full name</Label>
                    <IconInput
                      icon={User}
                      id="su-name"
                      autoComplete="name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Sanchia Thompson"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="su-email" required>
                      Email
                    </Label>
                    <IconInput
                      icon={Mail}
                      id="su-email"
                      type="email"
                      autoComplete="email"
                      placeholder="name@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="su-pw" required>
                      Password
                    </Label>
                    <PasswordInput
                      id="su-pw"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <PasswordChecklist password={password} />
                  </div>
                  <SubmitButton loading={loading}>
                    {loading ? "Creating…" : "Create account"}
                  </SubmitButton>
                  <p className="text-center text-xs text-muted-foreground">
                    The first account created becomes Super Administrator automatically.
                  </p>
                </form>
              </TabsContent>
            </Tabs>
          )}

          {view === "confirm" && (
            <form className="space-y-4" onSubmit={handleConfirm}>
              <div className="space-y-2">
                <Label htmlFor="cf-code" required>
                  Confirmation code
                </Label>
                <Input
                  id="cf-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  className="h-11 text-center text-lg tracking-[0.4em]"
                  required
                />
              </div>
              <SubmitButton loading={loading}>
                {loading ? "Confirming…" : "Confirm and sign in"}
              </SubmitButton>
              <p className="text-center text-sm text-muted-foreground">
                Didn't get it?{" "}
                <button
                  type="button"
                  onClick={handleResend}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  Resend the code
                </button>
              </p>
            </form>
          )}

          {view === "forgot" && (
            <form className="space-y-4" onSubmit={handleForgot}>
              <div className="space-y-2">
                <Label htmlFor="fp-email" required>
                  Email
                </Label>
                <IconInput
                  icon={Mail}
                  id="fp-email"
                  type="email"
                  autoComplete="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <SubmitButton loading={loading}>
                {loading ? "Sending…" : "Send reset code"}
              </SubmitButton>
            </form>
          )}

          {view === "reset" && (
            <form className="space-y-4" onSubmit={handleReset}>
              <div className="space-y-2">
                <Label htmlFor="rp-code" required>
                  Reset code
                </Label>
                <Input
                  id="rp-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  className="h-11 text-center text-lg tracking-[0.4em]"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rp-pw" required>
                  New password
                </Label>
                <PasswordInput
                  id="rp-pw"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <PasswordChecklist password={password} />
              </div>
              <SubmitButton loading={loading}>
                {loading ? "Updating…" : "Update password"}
              </SubmitButton>
              <p className="text-center text-sm text-muted-foreground">
                Didn't get it?{" "}
                <button
                  type="button"
                  onClick={handleResendReset}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  Resend the code
                </button>
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
